import React, { useRef } from "react";
import * as styles from "./device-select-panel-entry.module.scss";
import { VelavuDevice } from "velavu-js-api";
import NoAssetChip from "../no-asset-chip";
import DeviceIcon from "../device-icon";
import { getDeviceStatus } from "../../helper/status-helper";
import StatusIndicator from "../status-indicator";
import PopupSheetListEntry from "../popup-sheet/popup-sheet-list-entry";
import { VelavuTableColumn } from "../velavu-table-header";
import { useDelayHover } from "../../helper/hook-helper";
import BottomTooltip from "../bottom-tooltip";
import IconWarning from "../../dynamicicons/icon-warning";

export interface DeviceSelectPanelEntryProps {
	device: VelavuDevice;
	selected: boolean;
	showAssetInformation?: boolean;
	warning?: string;
	onClick: () => void;
}

function DeviceWarningTooltip(props: { warning: string }) {
	const [isHover, enableHover, disableHover] = useDelayHover();
	const ref = useRef<HTMLElement | null>(null);

	return (
		<span
			ref={ref}
			className={styles.warning}
			onMouseEnter={enableHover}
			onMouseLeave={disableHover}
		>
			<IconWarning />
			{isHover && (
				<BottomTooltip
					refPosition={ref.current}
					className={styles.warningText}
				>
					{props.warning}
				</BottomTooltip>
			)}
		</span>
	);
}

/**
 * A single selectable device entry in the DeviceSelectPanel
 */
export default function DeviceSelectPanelEntry(
	props: DeviceSelectPanelEntryProps,
) {
	return (
		<PopupSheetListEntry selected={props.selected} onClick={props.onClick}>
			<VelavuTableColumn sizing={{ width: 110 }}>
				<StatusIndicator
					status={getDeviceStatus(props.device)}
					className={styles.indicator}
				/>

				<span className={styles.deviceID}>{props.device.id}</span>

				{props.warning !== undefined && (
					<DeviceWarningTooltip warning={props.warning} />
				)}
			</VelavuTableColumn>

			<VelavuTableColumn
				sizing={
					props.showAssetInformation ? { width: 60 } : { flex: 1 }
				}
				alignment={props.showAssetInformation ? "center" : "start"}
			>
				<DeviceIcon model={props.device.model} />
				{!props.showAssetInformation && (
					<span className={styles.deviceName}>
						{props.device.model}
					</span>
				)}
			</VelavuTableColumn>

			{props.showAssetInformation && (
				<VelavuTableColumn sizing={{ flex: 1 }}>
					{props.device.asset !== undefined ? (
						<span className={styles.assetName}>
							{props.device.asset.name}
						</span>
					) : (
						<NoAssetChip />
					)}
				</VelavuTableColumn>
			)}
		</PopupSheetListEntry>
	);
}
